import type { UseQueryResult } from '@tanstack/react-query'
import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query'

import type { MarketProgram } from '@/features/bidding/constants'
import { findCapacityObligations } from '@/features/bidding/endpoints/bids'
import { useErrorHandler } from '@/features/bidding/hooks/useErrorHandler'
import type { CapacityObligation, CapacityObligations } from '@/features/bidding/types/capacityObligations'
import type { MarketDate } from '@/features/bidding/utils/date/marketDate'

const QUERY_KEY = 'CAPACITY_OBLIGATIONS'

export type UseCapacityObligationsQueryParams = Readonly<{
  portfolioCode: string | null
  deliveryDay: MarketDate | null
  marketProgram: MarketProgram
}>

export type UseCapacityObligationsQueryResult = Omit<UseQueryResult, 'data'> & {
  obligations: ReadonlyArray<CapacityObligation> | null
}

export function useCapacityObligationsQuery(
  params: UseCapacityObligationsQueryParams,
): UseCapacityObligationsQueryResult {
  const { portfolioCode, deliveryDay, marketProgram } = params

  const { data = null, ...queryResult } = useQuery({
    queryKey: [QUERY_KEY, portfolioCode, deliveryDay?.toISODate(), marketProgram],
    queryFn: async (): Promise<ReadonlyArray<CapacityObligation>> => {
      if (!portfolioCode || !deliveryDay || !marketProgram) {
        return []
      }
      const response: CapacityObligations | null = await findCapacityObligations(
        marketProgram,
        portfolioCode,
        deliveryDay,
      )

      if (!response) {
        return []
      }

      return response.obligations
    },
    enabled: portfolioCode !== null && deliveryDay !== null,
  })

  useErrorHandler(queryResult.isError)

  return { obligations: data, ...queryResult }
}

export function useInvalidateCapacityObligationsQuery() {
  const queryClient = useQueryClient()

  return () => queryClient.invalidateQueries({ queryKey: [QUERY_KEY] })
}

export const useIsFetchingCapacityObligations = (): boolean => useIsFetching({ queryKey: [QUERY_KEY] }) > 0
